import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowLeft, Save, Check, AlertTriangle, ShieldAlert, FileDown, Cloud } from 'lucide-react';
import FotoUpload from './FotoUpload';
import FotosOutros from './FotosOutros';
import OverlayProgresso from './OverlayProgresso';
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';
// Seletor de peça da Central de Pedidos (compartilhado com a Etapa 2 desde 2026-07-20).
import SelecaoPecaCentral from './SelecaoPecaCentral';
import useEhCompacto from '../../../utils/useEhCompacto';
import {
  itemNaoExiste, platensAusentes as calcPlatensAusentes, TIPOS_ENERGIA,
  eixosDoTemplate, ROTULO_EIXO, EIXOS_COM_ROTULO, chaveRotulo, eixoDoNome,
} from './blocosAusentes';

// =====================================================================
// Preenchimento do Relatório de Certificação — Etapa 1 (Diagnóstico).
// Identificação + avaliação dos componentes (Conforme/Desgaste/Crítico) +
// fotos ANTES + não conformidades. O Índice de Saúde é mostrado ao vivo
// (apenas indicativo) e RECALCULADO no servidor ao salvar (fonte da verdade).
//
// Fotos por UPLOAD SEPARADO: cada foto sobe na hora (POST /upload-foto) e
// guardamos só a URL; o /submit manda as URLs.
// =====================================================================

const API = '/api/manutencao/certificacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Ponteiro local do rascunho em andamento da Etapa 1 — usado para RETOMAR ao
// reabrir o módulo (banner no hub). Guarda o id do relatório + contexto p/ rótulo.
const LS_RASCUNHO_ETAPA1 = 'cert_rascunho_etapa1';
const guardarPonteiroRascunho = (info) => {
  try { localStorage.setItem(LS_RASCUNHO_ETAPA1, JSON.stringify(info)); } catch { /* storage indisponível: ignora */ }
};
const limparPonteiroRascunho = () => {
  try { localStorage.removeItem(LS_RASCUNHO_ETAPA1); } catch { /* ignora */ }
};

// Conforme=2 / Desgaste=1 / Crítico=0 (espelha o motor cert-score-utils).
const CLASSIF = [
  { v: 2, label: 'Conforme', cor: '#059669', bg: '#d1fae5' },
  { v: 1, label: 'Desgaste', cor: '#b45309', bg: '#fef3c7' },
  { v: 0, label: 'Crítico', cor: '#b91c1c', bg: '#fee2e2' },
];
// N/A (Não Aplicável) — só aparece em componente com PERMITE_NA. Sai do cálculo
// do Índice de Saúde (numerador e denominador), igual ao N/A da Auditoria.
const NAO_APLICAVEL = 3;
const OPCAO_NA = { v: NAO_APLICAVEL, label: 'N/A', cor: '#475569', bg: '#e2e8f0' };

// Largura fixa do botão de resposta. Cabe o rótulo mais longo ("Conforme") e é a
// mesma para todos — é o que mantém as colunas alinhadas de linha em linha,
// inclusive entre componentes que aceitam N/A e os que não aceitam.
const LARGURA_OPCAO = 88;
const estiloOpcao = (sel, cor, bg) => ({
  padding: '6px 4px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  border: sel ? `2px solid ${cor}` : '1px solid #cbd5e1',
  background: sel ? bg : '#fff', color: sel ? cor : '#64748b',
  whiteSpace: 'nowrap',
});

// Blocos ausentes (sistema a gás em equipamento elétrico, platens inexistentes):
// a regra é compartilhada com a Etapa 2 — ver blocosAusentes.js.
const sistemaNaoExiste = itemNaoExiste;
const corSaude = (c) => ({
  Excelente: '#059669', 'Muito Bom': '#4f46e5', Bom: '#65a30d',
  'Atenção': '#d97706', 'Crítico': '#dc2626',
}[c] || '#64748b');
// Motivo da compra (mesmos chips das peças substituídas da Etapa 2).
const MOTIVOS_PECA = ['Desgaste', 'Preventiva', 'Falha'];

const inputStyle = { width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#64748b' };
// Campo preenchido pelo catálogo da central — só leitura (troca-se a peça, não o texto).
const inputTravado = { ...inputStyle, marginTop: 0, background: '#f8fafc', color: '#334155' };

const CertificacaoQuestionario = ({ idTemplate, idRascunho = null, onVoltar }) => {
  const usuario = JSON.parse(localStorage.getItem('user') || '{}');
  const hoje = new Date().toISOString().slice(0, 10);
  // id do relatório já gravado (rascunho em andamento ou retomado). Quando setado,
  // o autosave e o submit final ATUALIZAM este relatório (não criam outro).
  const [idRelatorio, setIdRelatorio] = useState(idRascunho);
  const [estrutura, setEstrutura] = useState(null);
  const [gerentesPorLoja, setGerentesPorLoja] = useState({});
  const [ativos, setAtivos] = useState([]);          // equipamentos físicos cadastrados
  const [lojaSel, setLojaSel] = useState('');        // loja escolhida (ponto de partida)
  const [idAtivo, setIdAtivo] = useState('');        // equipamento escolhido (CODIGO, ex.: FRI-7K2P)
  // Chave única do relatório — agrupa as fotos no SharePoint (gerada uma vez).
  const [chave, setChave] = useState(() => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
  const [cab, setCab] = useState({
    // Identificação vem do ativo (snapshot, somente leitura na tela).
    // ⚠️ `modelo` é a exceção desde 2026-09-18: quando a VERSÃO declara uma
    // lista (Bebidas), ele vira <select> e o que o técnico escolhe é o que
    // vale. Sem lista, continua sendo o snapshot do ativo, só leitura.
    loja: '', fabricante: '', modelo: '', numero_serie: '', numero_ativo: '', ano_fabricacao: '',
    data_certificacao: hoje,
    // Especialista vem automático do usuário logado (pode editar).
    especialista_responsavel: usuario?.nome || '',
    gerente_negocios: '', gerente_plantao: '',
    // Dirigem o pré-N/A dos blocos que o equipamento não tem. Ficam vazios na
    // fritadeira, onde não se aplicam.
    // Só da chapa (→ NULL no banco):
    tipo_energia: '', qtd_platens: '',
    // Só das Bebidas: mapa EIXO → quantidade (SABOR/SISTEMA/TORRE/VALVULA).
    // Quais eixos a tela pede sai do PRÓPRIO template (ver eixosDoTemplate).
    quantidades: {},
    // Nome de cada bloco repetível: { 'VALVULA:7': 'Coca-Cola' }. Só SABOR e
    // VÁLVULA têm campo — ver EIXOS_COM_ROTULO.
    rotulos: {},
  });
  const [respostas, setRespostas] = useState({}); // id_componente → { classificacao, acao_observacao, fotos: [url...] (até 3) }
  const [ncEdit, setNcEdit] = useState({});       // id_componente → { criticidade, problema }
  const [pecasComprar, setPecasComprar] = useState([]); // lista de compras do diagnóstico
  const [fotosAntes, setFotosAntes] = useState({}); // slot → url (slots nomeados)
  const [fotosAntesOutros, setFotosAntesOutros] = useState([]); // até 3 fotos livres "Outros"
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const ehCompacto = useEhCompacto(); // celular/tablet: empilha os campos das peças

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const [rv, rg, ra] = await Promise.all([
        fetch(`${API}/versao/${idTemplate}`, { headers: auth() }),
        fetch(`${API}/gerentes`, { headers: auth() }),
        fetch('/api/manutencao/ativos?ativos=1', { headers: auth() }),
      ]);
      if (!rv.ok) throw new Error(`HTTP ${rv.status}`);
      setEstrutura(await rv.json());
      setGerentesPorLoja(rg.ok ? await rg.json() : {});
      setAtivos(ra.ok ? await ra.json() : []);

      // RESUME: retomando um rascunho → hidrata o formulário a partir do relatório
      // gravado (GET /:id). Como o autosave usa baseline, hidratar NÃO dispara
      // gravação — só mudanças do usuário a partir daqui é que salvam.
      if (idRascunho) {
        const rr = await fetch(`${API}/${idRascunho}`, { headers: auth() });
        const d = rr.ok ? await rr.json() : null;
        // Rascunho sumiu (apagado por um Admin, ou limpeza de teste): o formulário
        // abre vazio, mas é PRECISO soltar o id — ele nasce apontando para o
        // rascunho (useState(idRascunho)) e, se ficar, todo salvamento daqui em
        // diante tenta atualizar um registro inexistente e morre em 404.
        if (!d?.relatorio) {
          setIdRelatorio(null);
          limparPonteiroRascunho(); // para o banner "Continuar" não reoferecer
        }
        if (d?.relatorio) {
          const rel = d.relatorio;
          setIdRelatorio(rel.ID);
          setLojaSel(rel.LOJA || '');
          setIdAtivo(rel.ID_ATIVO || '');
          if (rel.CHAVE_ARQUIVOS) setChave(rel.CHAVE_ARQUIVOS); // novas fotos vão p/ a mesma pasta
          setCab({
            loja: rel.LOJA || '', fabricante: rel.FABRICANTE || '', modelo: rel.MODELO || '',
            numero_serie: rel.NUMERO_SERIE || '', numero_ativo: rel.NUMERO_ATIVO || '', ano_fabricacao: rel.ANO_FABRICACAO || '',
            data_certificacao: rel.DATA_CERTIFICACAO ? String(rel.DATA_CERTIFICACAO).slice(0, 10) : hoje,
            especialista_responsavel: rel.ESPECIALISTA_RESPONSAVEL || usuario?.nome || '',
            gerente_negocios: rel.GERENTE_NEGOCIOS || '', gerente_plantao: rel.GERENTE_PLANTAO || '',
            tipo_energia: rel.TIPO_ENERGIA || '', qtd_platens: rel.QTD_PLATENS || '',
            // Quantidades dos blocos repetíveis (Bebidas). Mapa EIXO → nº; vem
            // vazio em equipamento que não tem eixo repetível (Fritadeira, Chapa).
            quantidades: rel.QUANTIDADES || {},
            rotulos: rel.ROTULOS || {},
          });
          const resp = {};
          (d.componentes || []).forEach((c) => {
            resp[c.ID_COMPONENTE] = { classificacao: c.CLASSIFICACAO, acao_observacao: c.ACAO_OBSERVACAO || '', fotos: (c.FOTOS || []).filter(Boolean) };
          });
          setRespostas(resp);
          const nc = {};
          (d.naoConformidades || []).forEach((x) => { if (x.ID_COMPONENTE) nc[x.ID_COMPONENTE] = { problema: x.PROBLEMA || '', criticidade: x.CRITICIDADE || 'Média' }; });
          setNcEdit(nc);
          const fa = {}; const outros = [];
          (d.fotos || []).filter((f) => f.MOMENTO === 'antes').forEach((f) => {
            if (/^Outros\s/i.test(f.SLOT || '')) outros.push(f.FOTO_URL); else fa[f.SLOT] = f.FOTO_URL;
          });
          setFotosAntes(fa); setFotosAntesOutros(outros);
          // ORIGEM NULL = relatório anterior à feature de origem → trata como 'Fora' (digitada à mão).
          // Família/preço ref. vêm do JOIN com o catálogo (apoio visual — não são gravados na peça).
          setPecasComprar((d.pecasComprar || []).map((p) => ({
            origem: p.ORIGEM === 'Central' ? 'Central' : 'Fora', id_catalogo: p.ID_CATALOGO || null,
            codigo: p.CODIGO || '', fornecedor: p.FORNECEDOR || '', familia: p.CATALOGO_FAMILIA || '',
            descricao: p.DESCRICAO || '', quantidade: p.QUANTIDADE || 1, motivo: p.MOTIVO || '',
            preco_ref: p.CATALOGO_PRECO_REF ?? null,
          })));
        }
      }
    } catch (e) {
      setErro('Erro ao carregar o template.');
    } finally {
      setCarregando(false);
    }
    // hoje/usuario são estáveis na prática (data do dia / usuário logado) e ficam
    // fora das deps de propósito, para não recriar `carregar` a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTemplate, idRascunho]);

  useEffect(() => { carregar(); }, [carregar]);

  const setResp = (id, campo, valor) =>
    setRespostas((r) => ({ ...r, [id]: { ...r[id], [campo]: valor } }));

  // Ids que o pré-N/A marcou sozinho. Guardar isso é o que permite DESMARCAR
  // só o que foi automático quando o bloco volta a existir (ex.: o consultor
  // corrige de 1 para 3 platens) sem apagar um N/A que ele pôs à mão.
  const preNaAutomatico = useRef(new Set());

  // Lidos fora do efeito para o exhaustive-deps enxergar as dependências reais
  // (usar `cab.x` dentro do efeito faria o lint pedir o objeto `cab` inteiro,
  // o que reexecutaria o pré-N/A a cada tecla digitada no cabeçalho).
  const tipoEnergiaSel = cab.tipo_energia;
  const qtdPlatensSel = cab.qtd_platens;
  // Idem para as quantidades: como é um OBJETO, entra nas deps do efeito como
  // string estável — senão cada render recriaria a referência e o pré-N/A
  // rodaria de novo sem nada ter mudado.
  const quantidadesSel = cab.quantidades;
  const quantidadesKey = JSON.stringify(quantidadesSel || {});

  // Pré-N/A dos blocos inexistentes. É CONVENIÊNCIA, não trava: sem isto, uma
  // chapa de 1 platen exigiria marcar N/A à mão em até 25 componentes, toda vez.
  // O consultor pode corrigir qualquer resposta depois.
  useEffect(() => {
    if (!estrutura) return;
    setRespostas((r) => {
      const novo = { ...r };
      let mudou = false;
      for (const s of estrutura.sistemas || []) {
        const ausente = sistemaNaoExiste(s.NOME, {
          tipoEnergia: tipoEnergiaSel, qtdPlatens: qtdPlatensSel, quantidades: quantidadesSel,
        });
        for (const c of s.componentes || []) {
          // Componente sem N/A liberado no template não pode ser pré-marcado —
          // o backend recusaria a classificação.
          if (!c.PERMITE_NA) continue;
          if (ausente) {
            if (novo[c.ID]?.classificacao !== NAO_APLICAVEL) {
              novo[c.ID] = { acao_observacao: '', fotos: [], ...novo[c.ID], classificacao: NAO_APLICAVEL };
              preNaAutomatico.current.add(c.ID);
              mudou = true;
            }
          } else if (preNaAutomatico.current.has(c.ID)) {
            preNaAutomatico.current.delete(c.ID);
            novo[c.ID] = { ...novo[c.ID], classificacao: '' }; // volta a exigir resposta
            mudou = true;
          }
        }
      }
      return mudou ? novo : r; // sem mudança, devolve o mesmo objeto (não re-renderiza)
    });
    // `quantidadesSel` entra pela chave serializada (linha acima) de propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estrutura, tipoEnergiaSel, qtdPlatensSel, quantidadesKey]);

  // Só equipamentos do MESMO tipo do template (ex.: template Fritadeira → ativos Fritadeira).
  const ativosDoTipo = estrutura
    ? ativos.filter((a) => a.EQUIPAMENTO === estrutura.versao.EQUIPAMENTO)
    : [];

  // Identificação em 2 passos: LOJA → equipamento da loja. Cada loja tem mais de
  // um equipamento do mesmo tipo (ex.: fritadeira de Batatas e de Carnes Brancas),
  // por isso a loja sozinha não basta. Lojas = as que têm equipamento cadastrado.
  const lojasDisponiveis = [...new Set(ativosDoTipo.map((a) => a.LOJA).filter(Boolean))].sort();
  const ativosDaLoja = lojaSel ? ativosDoTipo.filter((a) => a.LOJA === lojaSel) : [];

  // Rótulo do equipamento na 2ª lista: usa a Denominação do SAP (é o que distingue
  // Batatas/Carnes); cai p/ modelo/patrimônio em ativos antigos sem denominação.
  const rotuloAtivo = (a) => a.DENOMINACAO || a.MODELO || `Patrimônio ${a.NUMERO_ATIVO}`;

  // Trocar de loja já libera os gerentes da loja e zera o equipamento escolhido.
  const selecionarLoja = (loja) => {
    setLojaSel(loja);
    setIdAtivo('');
    setCab((c) => ({
      ...c,
      loja,
      fabricante: '', modelo: '', numero_serie: '', numero_ativo: '', ano_fabricacao: '',
      gerente_negocios: '', gerente_plantao: '', // loja mudou → limpa gerentes
    }));
  };

  // Ao escolher o equipamento, copia a identificação (snapshot) para o cabeçalho.
  // O valor escolhido é o CODIGO do ativo (ex.: FRI-7K2P) — PK interna, não exibida.
  const selecionarAtivo = (id) => {
    setIdAtivo(id);
    const a = ativos.find((x) => String(x.CODIGO) === String(id));
    setCab((c) => ({
      ...c,
      loja: a?.LOJA || c.loja, fabricante: a?.FABRICANTE || '', modelo: a?.MODELO || '',
      numero_serie: a?.NUMERO_SERIE || '', numero_ativo: a?.NUMERO_ATIVO || '',
      ano_fabricacao: a?.ANO_FABRICACAO || '',
    }));
  };

  const gerentesDaLoja = cab.loja ? gerentesPorLoja[cab.loja] : null;

  // Quais campos de cabeçalho este template pede — lidos da própria estrutura,
  // não de uma lista de equipamentos no código.
  const sistemasDoTpl = estrutura?.sistemas || [];
  const temSistemaGas = sistemasDoTpl.some((s) => /g[áa]s/i.test(s.NOME || ''));
  const temPlatens = sistemasDoTpl.some((s) => /^Platen\s/i.test(s.NOME || ''));
  // Posições sem platen, para o aviso na tela (a marcação em si é o efeito acima).
  const platensAusentes = calcPlatensAusentes(cab.tipo_energia, cab.qtd_platens);
  // Eixos repetíveis (Bebidas): quais existem e até que índice. Sai da própria
  // estrutura, e o teto vira o `max` do campo — pedir 14 válvulas num template
  // que só declara 12 geraria bloco sem onde responder.
  const eixosTpl = useMemo(() => eixosDoTemplate(estrutura), [estrutura]);

  // Bloco de sistema que não existe no equipamento SOME da tela — não basta
  // marcar N/A: pedir para o consultor rolar por 25 componentes cinzas de um
  // platen que a chapa não tem é ruído puro. As respostas N/A continuam sendo
  // gravadas (o efeito acima preenche o estado), só não são exibidas.
  //
  // ⚠️ Só oculta se TODO componente do bloco puder ser N/A. Se algum não puder,
  // ele ficaria invisível E obrigatório — e o "Avalie todos os componentes"
  // travaria o salvamento sem mostrar onde está o problema.
  const sistemaOculto = (s) => sistemaNaoExiste(s.NOME, {
    tipoEnergia: cab.tipo_energia, qtdPlatens: cab.qtd_platens, quantidades: cab.quantidades,
  })
    && (s.componentes || []).length > 0
    && s.componentes.every((c) => c.PERMITE_NA);

  const sistemasVisiveis = sistemasDoTpl.filter((s) => !sistemaOculto(s));
  const sistemasOcultos = sistemasDoTpl.filter(sistemaOculto);

  // Em QUAL sistema aparece o campo de nome do bloco. Um rótulo por
  // (eixo, índice) — e não por sistema — porque o mesmo bloco pode estar
  // partido em dois: o SABOR tem "Controladores de porção" e "Linha", e pedir
  // o nome do sabor duas vezes na mesma tela seria ruído.
  const campoRotulo = useMemo(() => {
    const mapa = {}; const vistos = new Set();
    for (const s of sistemasVisiveis) {
      const b = eixoDoNome(s.NOME);
      if (!b || !EIXOS_COM_ROTULO[b.eixo]) continue;
      const chave = chaveRotulo(b.eixo, b.indice);
      if (vistos.has(chave)) continue;
      vistos.add(chave);
      mapa[s.ID] = { chave, eixo: b.eixo };
    }
    return mapa;
    // sistemasVisiveis é derivado a cada render; a identidade dos nomes é o que
    // importa, e ela só muda quando a estrutura ou as quantidades mudam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estrutura, cab.quantidades, cab.tipo_energia, cab.qtd_platens]);

  // Título do bloco com o nome que o técnico deu: "VÁLVULA 7 — Coca-Cola".
  // Ecoar o nome no cabeçalho é o que evita preencher a válvula errada numa
  // torre de doze blocos praticamente idênticos.
  const tituloDoSistema = (nome) => {
    const b = eixoDoNome(nome);
    if (!b) return nome;
    const texto = (cab.rotulos || {})[chaveRotulo(b.eixo, b.indice)];
    return texto ? `${nome} — ${texto}` : nome;
  };

  // Índice de Saúde ao vivo (indicativo) — mesma fórmula do motor do servidor.
  const { indiceVivo, classifVivo, temBloqVivo, todosRespondidos, totalComp } = useMemo(() => {
    if (!estrutura) return { indiceVivo: 0, classifVivo: '—', temBloqVivo: false, todosRespondidos: false, totalComp: 0 };
    let obt = 0, max = 0, bloq = false, respondidos = 0, total = 0;
    for (const s of estrutura.sistemas) {
      for (const c of s.componentes) {
        total += 1;
        const r = respostas[c.ID];
        if (r && r.classificacao !== undefined && r.classificacao !== '') {
          respondidos += 1;
          const cl = Number(r.classificacao);
          // N/A fica fora do numerador, do denominador e do bloqueante (mas conta como respondido).
          if (cl !== NAO_APLICAVEL) {
            obt += cl * s.PESO; max += 2 * s.PESO;
            if ((c.BLOQUEANTE === 1 || c.BLOQUEANTE === true) && cl === 0) bloq = true;
          }
        }
      }
    }
    const idx = max > 0 ? (obt / max) * 100 : 0;
    const faixa = idx >= 95 ? 'Excelente' : idx >= 90 ? 'Muito Bom' : idx >= 80 ? 'Bom' : idx >= 70 ? 'Atenção' : 'Crítico';
    return { indiceVivo: Number(idx.toFixed(2)), classifVivo: respondidos ? faixa : '—', temBloqVivo: bloq, todosRespondidos: total > 0 && respondidos === total, totalComp: total };
  }, [estrutura, respostas]);

  // Não conformidades = componentes avaliados que NÃO são Conforme (≠ 2).
  const naoConformes = useMemo(() => {
    if (!estrutura) return [];
    const lista = [];
    for (const s of estrutura.sistemas) {
      for (const c of s.componentes) {
        const r = respostas[c.ID];
        if (r && (Number(r.classificacao) === 1 || Number(r.classificacao) === 0)) {
          lista.push({ id: c.ID, nome: c.NOME, sistema: s.NOME, critico: Number(r.classificacao) === 0 });
        }
      }
    }
    return lista;
  }, [estrutura, respostas]);

  // Monta o corpo do POST /submit. rascunho=true → autosave (parcial, sem PDF/e-mail).
  // É a fonte ÚNICA do payload (autosave e submit final), p/ não divergirem.
  const montarPayload = (rascunho) => ({
    id_template: idTemplate,
    ...(idRelatorio ? { id_relatorio: idRelatorio } : {}),
    id_ativo: idAtivo, // CODIGO do ativo (string, ex.: FRI-7K2P)
    chave_arquivos: chave,
    ...cab,
    // Só os componentes JÁ avaliados (no autosave o formulário está parcial).
    respostas: Object.entries(respostas)
      .filter(([, r]) => r && r.classificacao !== undefined && r.classificacao !== '')
      .map(([id, r]) => ({
        id_componente: Number(id), classificacao: Number(r.classificacao),
        acao_observacao: r.acao_observacao || null,
        fotos: (r.fotos || []).filter(Boolean), // até 3 fotos do componente
      })),
    nao_conformidades: naoConformes.map((nc) => ({
      id_componente: nc.id, componente: nc.nome,
      problema: ncEdit[nc.id]?.problema || null,
      criticidade: ncEdit[nc.id]?.criticidade || (nc.critico ? 'Alta' : 'Média'),
    })),
    // Slots nomeados (inclui Etiqueta, que pode vir vazia) + as fotos "Outros".
    fotos_antes: [
      ...(estrutura?.slotsFotoAntes || []).map((s) => ({ slot: s, foto_url: fotosAntes[s] })),
      ...fotosAntesOutros.filter(Boolean).map((u, i) => ({ slot: `Outros ${i + 1}`, foto_url: u })),
    ],
    // Só os campos que o backend grava (preco_ref é apoio visual, fica de fora).
    pecas_comprar: pecasComprar
      .filter((p) => p.descricao?.trim() || p.motivo || p.fornecedor?.trim())
      .map((p) => ({
        origem: p.origem === 'Central' ? 'Central' : 'Fora',
        id_catalogo: p.origem === 'Central' ? (p.id_catalogo || null) : null,
        codigo: p.codigo, fornecedor: p.fornecedor, descricao: p.descricao,
        quantidade: p.quantidade, motivo: p.motivo,
      })),
    ...(rascunho ? { rascunho: true } : {}),
  });

  // Envia o formulário, com RECUPERAÇÃO de rascunho apagado.
  //
  // O payload leva `id_relatorio` para atualizar o mesmo registro criado pelo
  // autosave. Se esse registro sumiu do banco (um Admin apagou, ou foi limpeza
  // de teste), o backend devolve 404 "Relatório não encontrado" e, sem
  // tratamento, a tela entra em beco sem saída: toda tentativa de salvar falha
  // e o preenchimento inteiro — que pode ser 80+ componentes e fotos — se perde.
  //
  // Aqui o 404 é tratado como "o destino não existe mais": descarta o ponteiro
  // órfão e reenvia UMA vez como relatório NOVO. Não há risco de duplicar,
  // porque 404 significa justamente que não existe linha para duplicar.
  const enviarFormulario = async (rascunho) => {
    const postar = (comId) => fetch(`${API}/submit`, {
      method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(comId ? montarPayload(rascunho) : { ...montarPayload(rascunho), id_relatorio: undefined }),
    });
    let r = await postar(true);
    let data = await r.json();
    if (r.status === 404 && idRelatorio) {
      limparPonteiroRascunho();
      setIdRelatorio(null);
      r = await postar(false);
      data = await r.json();
    }
    if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
    return data;
  };

  // Autosave: grava o rascunho silenciosamente (sem validar, sem PDF/e-mail).
  // Guarda o id devolvido (a 1ª gravação cria o relatório) e o ponteiro local
  // para retomar ao reabrir o módulo. Lança em erro (o hook mostra o estado).
  const salvarRascunho = async () => {
    const data = await enviarFormulario(true);
    if (data.id && data.id !== idRelatorio) setIdRelatorio(data.id);
    guardarPonteiroRascunho({
      id: data.id, idTemplate,
      equipamento: estrutura?.versao?.EQUIPAMENTO || '', loja: cab.loja || '',
    });
  };

  // Snapshot do conteúdo (string) — o autosave só dispara quando isto muda.
  const snapshot = useMemo(
    () => JSON.stringify({ idAtivo, cab, respostas, ncEdit, pecasComprar, fotosAntes, fotosAntesOutros }),
    [idAtivo, cab, respostas, ncEdit, pecasComprar, fotosAntes, fotosAntesOutros],
  );
  // Autosave ligado só após carregar, com equipamento escolhido (o back exige
  // id_ativo) e enquanto não finalizou (sem a tela de resultado).
  const autosaveAtivo = !carregando && !!idAtivo && !resultado;
  const { estado: autosaveEstado } = useAutosaveRascunho({ ativo: autosaveAtivo, snapshot, salvarFn: salvarRascunho });

  const salvar = async () => {
    setErro(''); setResultado(null);
    if (!idAtivo) { setErro('Selecione o equipamento.'); return; }
    if (!todosRespondidos) { setErro('Avalie todos os componentes antes de salvar.'); return; }
    // Só os slots OBRIGATÓRIOS contam (Etiqueta/Patrimônio é opcional).
    const slotsObrig = estrutura.slotsFotoAntesObrigatorios || estrutura.slotsFotoAntes || [];
    const slotsFaltando = slotsObrig.filter((s) => !fotosAntes[s]);
    if (slotsFaltando.length > 0) { setErro(`Fotos ANTES obrigatórias faltando: ${slotsFaltando.join(', ')}.`); return; }
    // Pelo menos 1 foto nos componentes Desgaste/Crítico (naoConformes = classif 1/0).
    const semFoto = naoConformes.filter((nc) => !(respostas[nc.id]?.fotos || []).filter(Boolean).length);
    if (semFoto.length > 0) { setErro(`Anexe foto nos componentes Desgaste/Crítico: ${semFoto.map((c) => c.nome).join(', ')}.`); return; }

    setSalvando(true);
    try {
      // Submit final: valida tudo no servidor e promove o rascunho a 'Em diagnóstico'.
      // id_relatorio vai no payload (via montarPayload) → atualiza o MESMO relatório
      // criado pelo autosave, sem duplicar. Se o rascunho tiver sido apagado no
      // meio do caminho, enviarFormulario reenvia como novo em vez de falhar.
      const data = await enviarFormulario(false);
      limparPonteiroRascunho(); // diagnóstico finalizado → não há mais rascunho a retomar
      setResultado(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  // Download do PDF do diagnóstico (a rota exige token → fetch + blob).
  const baixarPdf = async (id) => {
    try {
      const resp = await fetch(`${API}/${id}/pdf`, { headers: auth() });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = `Diagnostico_${id}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { alert('Erro ao gerar o PDF.'); }
  };

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando…</p>;
  if (!estrutura) return (
    <div>
      <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ marginTop: 12, padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erro || 'Template indisponível.'}</div>
    </div>
  );

  if (resultado) {
    return (
      <div>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <div style={{ marginTop: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          <Check size={40} color="#059669" />
          <h3 style={{ margin: '12px 0 4px', color: '#0f172a' }}>Diagnóstico salvo</h3>
          <p style={{ color: '#64748b', margin: 0 }}>Relatório #{resultado.id} — status “Em diagnóstico”.</p>
          <div style={{ marginTop: 16, fontSize: 28, fontWeight: 800, color: corSaude(resultado.classificacaoSaude) }}>
            {resultado.indiceSaude}% · {resultado.classificacaoSaude}
          </div>
          {resultado.temBloqueanteCritico && (
            <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontWeight: 700 }}>
              <ShieldAlert size={16} /> Item bloqueante Crítico — não certificável: {resultado.bloqueantesCriticos?.join(', ')}
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <button onClick={() => baixarPdf(resultado.id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
              background: '#fff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8,
              fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>
              <FileDown size={16} /> Baixar PDF do diagnóstico
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {salvando && <OverlayProgresso titulo="Salvando diagnóstico…" subtitulo="Gerando o PDF — pode levar alguns segundos." />}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {estrutura.versao.EQUIPAMENTO} · {estrutura.versao.NOME_VERSAO}
        </h3>
      </div>

      {/* Painel fixo do Índice de Saúde ao vivo */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5, marginBottom: 16, padding: 14, borderRadius: 12,
        background: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Índice de Saúde (prévia)</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: corSaude(classifVivo) }}>{indiceVivo}% · {classifVivo}</div>
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{Object.keys(respostas).filter((k) => respostas[k]?.classificacao !== undefined && respostas[k]?.classificacao !== '').length}/{totalComp} componentes</div>
        {temBloqVivo && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontWeight: 700, fontSize: 12 }}>
            <ShieldAlert size={14} /> Bloqueante Crítico
          </div>
        )}
        {/* Estado do autosave de rascunho — só aparece quando o autosave está ligado. */}
        {autosaveAtivo && (
          <span style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
            color: autosaveEstado === 'erro' ? '#b91c1c' : autosaveEstado === 'salvando' ? '#64748b' : '#059669',
          }}>
            {autosaveEstado === 'salvando' ? <><Cloud size={14} /> Salvando rascunho…</>
              : autosaveEstado === 'salvo' ? <><Check size={14} /> Rascunho salvo</>
                : autosaveEstado === 'erro' ? <><AlertTriangle size={14} /> Falha ao salvar rascunho</>
                  : <><Cloud size={14} /> Rascunho automático ligado</>}
          </span>
        )}
      </div>

      {/* Erro de validação / salvamento aparece como POP-UP (modal) — antes era uma
          faixa inline que passava batida quando o usuário estava no fim do formulário. */}
      {erro && (
        <div onClick={() => setErro('')} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          {/* stopPropagation: clicar dentro do card não fecha; só o fundo/botão fecham. */}
          <div onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true" style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 440,
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{ width: 48, height: 48, margin: '0 auto 12px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={26} color="#b91c1c" />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Não foi possível salvar</h3>
            <p style={{ margin: '0 0 20px', color: '#475569', fontSize: 14, lineHeight: 1.5 }}>{erro}</p>
            <button onClick={() => setErro('')} style={{
              padding: '10px 28px', background: '#4f46e5', color: '#fff', border: 'none',
              borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 14,
            }}>Entendi</button>
          </div>
        </div>
      )}

      {/* Painel introdutório — explica o relatório, a pontuação e a métrica */}
      <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, color: '#4338ca', marginBottom: 6 }}>Sobre este relatório — Etapa 1 (Diagnóstico)</div>
        <p style={{ margin: '0 0 8px' }}>
          Avalie cada componente do equipamento. A <strong>Etapa 1</strong> registra o diagnóstico e o Índice de Saúde;
          a <strong>Etapa 2</strong> (certificação) é feita depois do reparo, na tela de consulta.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
          <div>
            <strong>Modelo de pontuação</strong><br />
            Conforme = 2 · Desgaste = 1 · Crítico = 0. Cada componente herda o <strong>peso do seu sistema</strong>.
            Índice de Saúde = pontos obtidos ÷ máximos.
          </div>
          <div>
            <strong>Classificação</strong><br />
            ≥95% Excelente · 90–94% Muito Bom · 80–89% Bom · 70–79% Atenção · &lt;70% Crítico.
          </div>
          <div>
            <strong>Certificação</strong><br />
            ≥85% Certificado · 70–84% Com Restrições · &lt;70% Não Certificado.
            Um item <strong>bloqueante</strong> Crítico reprova, mesmo com % alto.
          </div>
        </div>
      </div>

      {/* 1) Identificação — escolhe o equipamento cadastrado (snapshot vem do cadastro) */}
      <Secao titulo="Identificação do equipamento">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <Campo label="Loja *">
            <select value={lojaSel} onChange={(e) => selecionarLoja(e.target.value)} style={inputStyle}>
              <option value="">— escolher —</option>
              {lojasDisponiveis.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            {ativosDoTipo.length === 0 && (
              <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                Nenhum equipamento deste tipo cadastrado. Cadastre em “Equipamentos”.
              </div>
            )}
          </Campo>
          <Campo label={`${estrutura.versao.EQUIPAMENTO} *`}>
            {/* 2º passo: equipamento da loja (ex.: fritadeira de Batatas / Carnes Brancas). */}
            <select value={idAtivo} disabled={!lojaSel} onChange={(e) => selecionarAtivo(e.target.value)} style={inputStyle}>
              <option value="">{lojaSel ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {ativosDaLoja.map((a) => (
                <option key={a.CODIGO} value={a.CODIGO}>
                  {`${rotuloAtivo(a)} · Patrimônio ${a.NUMERO_ATIVO}`}
                </option>
              ))}
            </select>
          </Campo>
          {/* Tipo de energia e nº de platens só existem em equipamento que tem
              esses blocos — descoberto pelo próprio template, para não amarrar
              a tela ao nome "Chapa". Na fritadeira nem aparecem. */}
          {temSistemaGas && (
            <Campo label="Tipo de energia">
              <select style={inputStyle} value={cab.tipo_energia}
                onChange={(e) => setCab({ ...cab, tipo_energia: e.target.value })}>
                <option value="">— escolher —</option>
                {TIPOS_ENERGIA.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {cab.tipo_energia === 'Elétrica' && (
                <div style={{ fontSize: 11, color: '#4338ca', marginTop: 4 }}>
                  Sistema a gás ocultado e registrado como N/A.
                </div>
              )}
            </Campo>
          )}
          {temPlatens && (
            <Campo label="Quantidade de platens">
              <select style={inputStyle} value={cab.qtd_platens}
                onChange={(e) => setCab({ ...cab, qtd_platens: e.target.value })}>
                <option value="">— escolher —</option>
                <option value="1">1 platen</option>
                <option value="2">2 platens</option>
                <option value="3">3 platens</option>
              </select>
              {platensAusentes.length > 0 && (
                <div style={{ fontSize: 11, color: '#4338ca', marginTop: 4 }}>
                  Platen {platensAusentes.join(' e ')} ocultado(s) e registrado(s) como N/A.
                </div>
              )}
            </Campo>
          )}
          {/* Blocos repetíveis do Sistema de Bebidas (sabores, sistemas, torres,
              válvulas). Mesmo desenho dos platens e pelo mesmo motivo: o
              template traz o máximo da rede e a quantidade declarada aqui esconde
              o resto, que vai a N/A e sai do Índice de Saúde.
              É <select> e não <input> de propósito — o teto vem do template, e
              digitar 14 num template de 12 válvulas criaria bloco sem onde
              responder. Ver eixosDoTemplate em blocosAusentes.js. */}
          {Object.entries(eixosTpl).map(([eixo, teto]) => {
            const escolhido = Number(cab.quantidades?.[eixo]) || 0;
            const ocultos = escolhido ? teto - escolhido : 0;
            return (
              <Campo key={eixo} label={`Quantidade de ${ROTULO_EIXO[eixo]}`}>
                <select style={inputStyle} value={cab.quantidades?.[eixo] || ''}
                  onChange={(e) => setCab({
                    ...cab,
                    quantidades: { ...cab.quantidades, [eixo]: e.target.value },
                  })}>
                  <option value="">— escolher —</option>
                  {Array.from({ length: teto }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                {ocultos > 0 && (
                  <div style={{ fontSize: 11, color: '#4338ca', marginTop: 4 }}>
                    {ocultos} bloco(s) ocultado(s) e registrado(s) como N/A.
                  </div>
                )}
              </Campo>
            );
          })}
          {/* Modelo: vira ESCOLHA quando a versão declara opções (Bebidas:
              Quest/Quantum 4000 · Vanguard/RM8000/Multiplan). Sem lista, o
              modelo segue no snapshot do ativo e este campo nem aparece. */}
          {(estrutura?.modelos || []).length > 0 && (
            <Campo label="Modelo">
              <select style={inputStyle} value={cab.modelo || ''}
                onChange={(e) => setCab({ ...cab, modelo: e.target.value })}>
                <option value="">— escolher —</option>
                {estrutura.modelos.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Campo>
          )}
          <Campo label="Data da Certificação"><input type="date" style={inputStyle} value={cab.data_certificacao} onChange={(e) => setCab({ ...cab, data_certificacao: e.target.value })} /></Campo>
          <Campo label="Especialista Responsável"><input style={inputStyle} value={cab.especialista_responsavel} onChange={(e) => setCab({ ...cab, especialista_responsavel: e.target.value })} /></Campo>
          <Campo label="Gerente de Negócios">
            <select value={cab.gerente_negocios} disabled={!gerentesDaLoja} onChange={(e) => setCab({ ...cab, gerente_negocios: e.target.value })} style={inputStyle}>
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.negocios || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Campo>
          <Campo label="Gerente de Plantão">
            <select value={cab.gerente_plantao} disabled={!gerentesDaLoja} onChange={(e) => setCab({ ...cab, gerente_plantao: e.target.value })} style={inputStyle}>
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.plantao || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Campo>
        </div>
        {idAtivo && (
          <div style={{ marginTop: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#475569', display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
            <span><strong>Loja:</strong> {cab.loja || '-'}</span>
            <span><strong>Fabricante:</strong> {cab.fabricante || '-'}</span>
            <span><strong>Modelo:</strong> {cab.modelo || '-'}</span>
            <span><strong>Nº Série:</strong> {cab.numero_serie || '-'}</span>
            <span><strong>Ano:</strong> {cab.ano_fabricacao || '-'}</span>
          </div>
        )}
      </Secao>

      {/* 2) Diagnóstico por sistema */}
      {/* Blocos omitidos: dito em voz alta, nunca em silêncio — o consultor
          precisa saber que aquilo foi respondido como N/A, não esquecido. */}
      {sistemasOcultos.length > 0 && (
        <div style={{
          padding: '10px 14px', background: '#eef2ff', border: '1px solid #c7d2fe',
          borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#3730a3',
        }}>
          <strong>{sistemasOcultos.length} bloco(s) ocultado(s)</strong> — este equipamento não os
          possui, e todos os itens foram registrados como N/A:{' '}
          {sistemasOcultos.map((s) => s.NOME).join(' · ')}
        </div>
      )}

      {sistemasVisiveis.map((s) => (
        <Secao key={s.ID} titulo={tituloDoSistema(s.NOME)} sub={`peso ${s.PESO}`}>
          <div style={{ display: 'grid', gap: 10 }}>
            {/* Nome do que está NESTE bloco — o sabor da linha, o produto da
                válvula. É o que o papel pede ("VÁLVULA ___ — PRODUTO: ____") e
                o que faz o certificado dizer qual bebida está com problema.
                Opcional de propósito: bloco sem nome ainda é um bloco válido. */}
            {campoRotulo[s.ID] && (
              <input
                style={{ ...inputStyle, maxWidth: 320 }}
                placeholder={`${EIXOS_COM_ROTULO[campoRotulo[s.ID].eixo]} (opcional)`}
                value={cab.rotulos?.[campoRotulo[s.ID].chave] || ''}
                onChange={(e) => setCab({
                  ...cab,
                  rotulos: { ...cab.rotulos, [campoRotulo[s.ID].chave]: e.target.value },
                })}
              />
            )}
            {s.componentes.map((c) => {
              const r = respostas[c.ID] || {};
              return (
                <div key={c.ID} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, minWidth: 180, fontWeight: 600, color: '#0f172a' }}>
                      {c.NOME}
                      {(c.BLOQUEANTE === 1 || c.BLOQUEANTE === true) && (
                        <span title="Componente bloqueante: Crítico aqui reprova a certificação" style={{ marginLeft: 6, color: '#b91c1c', verticalAlign: 'middle' }}>
                          <ShieldAlert size={14} />
                        </span>
                      )}
                    </span>
                    {/* Grade de largura FIXA (4 colunas) em vez de flex: assim toda
                        linha ocupa o mesmo espaço, tenha ou não N/A. Com flex, o
                        rótulo mais curto encolhia o botão e as colunas dançavam de
                        um componente para o outro. A 4ª coluna fica reservada e
                        vazia quando o componente não aceita N/A. */}
                    <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? 'repeat(2, 1fr)' : `repeat(4, ${LARGURA_OPCAO}px)`, gap: 6 }}>
                      {CLASSIF.map((opt) => {
                        const sel = Number(r.classificacao) === opt.v;
                        return (
                          <button key={opt.v} onClick={() => setResp(c.ID, 'classificacao', opt.v)}
                            style={estiloOpcao(sel, opt.cor, opt.bg)}>
                            {opt.label}
                          </button>
                        );
                      })}
                      {/* N/A só nos componentes do template marcados com PERMITE_NA. */}
                      {c.PERMITE_NA ? (
                        <button onClick={() => setResp(c.ID, 'classificacao', OPCAO_NA.v)}
                          style={estiloOpcao(Number(r.classificacao) === OPCAO_NA.v, OPCAO_NA.cor, OPCAO_NA.bg)}>
                          {OPCAO_NA.label}
                        </button>
                      ) : <span aria-hidden="true" />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      {/* Até 3 fotos por componente (1 obrigatória se Desgaste/Crítico). */}
                      <FotosOutros fotos={r.fotos} onChange={(f) => setResp(c.ID, 'fotos', f)} max={3}
                        patrimonio={cab.numero_ativo} chave={chave} prefixoSlot={c.NOME} placeholderVazio={null} />
                      {/* Desgaste(1)/Crítico(0) exige ao menos 1 foto — aviso até anexar. */}
                      {(Number(r.classificacao) === 1 || Number(r.classificacao) === 0) && !(r.fotos || []).filter(Boolean).length && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#b91c1c' }}>foto obrigatória</span>
                      )}
                    </div>
                  </div>
                  {(Number(r.classificacao) === 1 || Number(r.classificacao) === 0) && (
                    <input placeholder="Ação recomendada / observação"
                      value={r.acao_observacao || ''} onChange={(e) => setResp(c.ID, 'acao_observacao', e.target.value)}
                      style={{ ...inputStyle, marginTop: 8 }} />
                  )}
                </div>
              );
            })}
          </div>
        </Secao>
      ))}

      {/* 3) Fotos ANTES — quantas são obrigatórias depende do EQUIPAMENTO
          (Etiqueta/Patrimônio é opcional sempre; a chapa também não deve o
          'Sistema de Filtragem'). ⚠️ O subtítulo CONTA a lista que veio do
          servidor em vez de dizer "5": na chapa são 4, e um número fixo brigaria
          com os "(opcional)" marcados logo abaixo. */}
      <Secao titulo="Registro fotográfico — ANTES" sub={`${(estrutura.slotsFotoAntesObrigatorios || estrutura.slotsFotoAntes || []).length} obrigatórias + opcionais`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {(estrutura.slotsFotoAntes || []).map((slot) => {
            const obrigatorio = (estrutura.slotsFotoAntesObrigatorios || estrutura.slotsFotoAntes || []).includes(slot);
            return (
              <div key={slot} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 8, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#475569' }}>
                  {slot}{!obrigatorio && <span style={{ color: '#94a3b8', fontWeight: 400 }}> (opcional)</span>}
                </span>
                <FotoUpload url={fotosAntes[slot]} label={fotosAntes[slot] ? 'OK' : 'Foto'} onUrl={(u) => setFotosAntes((f) => ({ ...f, [slot]: u }))}
                  patrimonio={cab.numero_ativo} chave={chave} slot={`antes_${slot}`} />
              </div>
            );
          })}
        </div>
        {/* Outros — até 3 fotos livres (opcionais) */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Outros <span style={{ color: '#94a3b8', fontWeight: 400 }}>(até 3, opcional)</span></span>
          <FotosOutros fotos={fotosAntesOutros} onChange={setFotosAntesOutros} max={3}
            patrimonio={cab.numero_ativo} chave={chave} prefixoSlot="antes_Outros" />
        </div>
      </Secao>

      {/* 4) Não conformidades (derivadas do que não é Conforme) */}
      <Secao titulo="Não Conformidades" sub={`${naoConformes.length} item(ns)`}>
        {naoConformes.length === 0 ? (
          <p style={{ color: '#64748b', margin: 0 }}>Nenhuma — marque componentes como Desgaste/Crítico para gerar.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {naoConformes.map((nc) => (
              <div key={nc.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ minWidth: 200, fontSize: 13, fontWeight: 600, color: nc.critico ? '#b91c1c' : '#b45309' }}>
                  <AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> {nc.sistema} · {nc.nome}
                </span>
                <input placeholder="Problema" value={ncEdit[nc.id]?.problema || ''}
                  onChange={(e) => setNcEdit((m) => ({ ...m, [nc.id]: { ...m[nc.id], problema: e.target.value } }))}
                  style={{ flex: 1, minWidth: 160, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                <select value={ncEdit[nc.id]?.criticidade || (nc.critico ? 'Alta' : 'Média')}
                  onChange={(e) => setNcEdit((m) => ({ ...m, [nc.id]: { ...m[nc.id], criticidade: e.target.value } }))}
                  style={{ padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                  <option>Alta</option><option>Média</option><option>Baixa</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </Secao>

      {/* 5) Peças a comprar — lista de compras do diagnóstico.
          Na Etapa 2 (certificação) vira a base de "Peças substituídas".
          Origem da compra: 'Central' = escolhida no catálogo da Central de
          Pedidos (código/descrição/fornecedor preenchem sozinhos); 'Fora' =
          peça fora da central, digitada à mão. */}
      <Secao titulo="Peças a comprar" sub="lista de compras — o valor é confirmado na certificação">
        {pecasComprar.length === 0 ? (
          <p style={{ color: '#94a3b8', margin: 0, fontSize: 13 }}>Nenhuma peça. Use o botão abaixo para adicionar.</p>
        ) : pecasComprar.map((p, i) => {
          const setP = (patch) => setPecasComprar((arr) => arr.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          const remover = (
            <button onClick={() => setPecasComprar((arr) => arr.filter((_, j) => j !== i))} title="Remover"
              style={{ justifySelf: 'end', padding: '6px 12px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, cursor: 'pointer' }}>×</button>
          );
          return (
            <div key={i} style={{ border: '1px solid #f1f5f9', borderRadius: 8, padding: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Origem:</span>
                {[['Central', 'Central de Pedidos'], ['Fora', 'Fora da central']].map(([o, rotulo]) => {
                  const sel = p.origem === o;
                  return (
                    <button key={o} onClick={() => {
                      // Trocar a origem zera a peça — não vaza dado do catálogo pro modo manual (e vice-versa).
                      if (!sel) setP({ origem: o, id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '', preco_ref: null });
                    }} style={{
                      padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: sel ? '2px solid #4338ca' : '1px solid #cbd5e1',
                      background: sel ? '#e0e7ff' : '#fff', color: sel ? '#4338ca' : '#64748b',
                    }}>{rotulo}</button>
                  );
                })}
              </div>

              {/* Central, peça ainda não escolhida → listas suspensas Fornecedor → Família → Peça */}
              {p.origem === 'Central' && !p.id_catalogo && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <SelecaoPecaCentral onEscolher={(r) => setP({
                    id_catalogo: r.ID, codigo: r.CODIGO, descricao: r.DESCRICAO,
                    fornecedor: r.FORNECEDOR, familia: r.FAMILIA || '', preco_ref: r.PRECO_REF,
                  })} />
                  {remover}
                </div>
              )}

              {/* Central, peça escolhida → código/fornecedor/família/descrição separados e
                  travados (snapshot do catálogo) + quantidade editável */}
              {p.origem === 'Central' && p.id_catalogo && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '90px 120px 130px 1fr 70px 32px', gap: 6, alignItems: 'end' }}>
                    <div><span style={labelStyle}>Código</span><input style={inputTravado} readOnly value={p.codigo || ''} /></div>
                    <div><span style={labelStyle}>Fornecedor</span><input style={inputTravado} readOnly value={p.fornecedor || ''} /></div>
                    <div><span style={labelStyle}>Família</span><input style={inputTravado} readOnly value={p.familia || ''} /></div>
                    <div><span style={labelStyle}>Descrição</span><input style={inputTravado} readOnly value={p.descricao || ''} /></div>
                    <div>
                      <span style={labelStyle}>Qtd</span>
                      <input style={{ ...inputStyle, marginTop: 0 }} type="number" min="1" placeholder="Qtd" value={p.quantidade}
                        onChange={(e) => setP({ quantidade: e.target.value })} />
                    </div>
                    {remover}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    {p.preco_ref != null && (
                      <span style={{ fontSize: 12, color: '#64748b' }}>Preço ref.: R$ {Number(p.preco_ref).toFixed(2)}</span>
                    )}
                    <button onClick={() => setP({ id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '', preco_ref: null })}
                      style={{ padding: '2px 8px', background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      trocar peça
                    </button>
                  </div>
                </>
              )}

              {/* Fora da central → digitação livre (comportamento original) */}
              {p.origem !== 'Central' && (
                <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '100px 140px 1fr 70px 32px', gap: 6, alignItems: 'center' }}>
                  {/* Código é opcional (rótulo deixa isso claro). */}
                  <input style={{ ...inputStyle, marginTop: 0 }} placeholder="Código (opcional)" value={p.codigo || ''}
                    onChange={(e) => setP({ codigo: e.target.value })} />
                  <input style={{ ...inputStyle, marginTop: 0 }} placeholder="Fornecedor" value={p.fornecedor || ''}
                    onChange={(e) => setP({ fornecedor: e.target.value })} />
                  <input style={{ ...inputStyle, marginTop: 0 }} placeholder="Descrição" value={p.descricao}
                    onChange={(e) => setP({ descricao: e.target.value })} />
                  <input style={{ ...inputStyle, marginTop: 0 }} type="number" min="1" placeholder="Qtd" value={p.quantidade}
                    onChange={(e) => setP({ quantidade: e.target.value })} />
                  {remover}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Motivo:</span>
                {MOTIVOS_PECA.map((m) => {
                  const sel = p.motivo === m;
                  return (
                    <button key={m} onClick={() => setP({ motivo: m })} style={{
                      padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: sel ? '2px solid #4338ca' : '1px solid #cbd5e1',
                      background: sel ? '#e0e7ff' : '#fff', color: sel ? '#4338ca' : '#64748b',
                    }}>{m}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <button onClick={() => setPecasComprar((p) => [...p, { origem: 'Central', id_catalogo: null, codigo: '', fornecedor: '', familia: '', descricao: '', quantidade: 1, motivo: '' }])} style={{
          marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px',
          background: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
        }}>+ Peça</button>
      </Secao>

      <button onClick={salvar} disabled={salvando} style={{
        marginTop: 8, padding: '12px 24px', background: salvando ? '#94a3b8' : '#4f46e5', color: '#fff',
        border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15,
      }}>
        <Save size={18} /> {salvando ? 'Salvando…' : 'Salvar diagnóstico'}
      </button>
    </div>
  );
};

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

const Secao = ({ titulo, sub, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
    <h4 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: '#475569' }}>
      {titulo} {sub && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>· {sub}</span>}
    </h4>
    {children}
  </div>
);

const Campo = ({ label, children }) => (
  <div><label style={labelStyle}>{label}</label>{children}</div>
);

export default CertificacaoQuestionario;
